/**
 * FloatingActionButton - Gradient pill FAB for creating new items.
 *
 * Positioned fixed at bottom-right. Pill shape with module accent color gradient.
 * Shows a label with the action name. Scales on hover with spring animation.
 */

interface FloatingActionButtonProps {
  onClick: () => void;
  label: string;
  /** Gradient color scheme - matches module accent */
  gradient?: 'purple' | 'amber' | 'teal' | 'emerald' | 'pink' | 'indigo';
}

const gradientMap = {
  purple: 'from-violet-500 via-fuchsia-500 to-pink-500',
  amber: 'from-amber-400 via-orange-500 to-pink-500',
  teal: 'from-cyan-400 via-sky-500 to-teal-500',
  emerald: 'from-lime-400 via-emerald-500 to-green-500',
  pink: 'from-fuchsia-500 via-pink-500 to-rose-500',
  indigo: 'from-indigo-400 via-violet-500 to-purple-600',
};

const shadowMap = {
  purple: '0 10px 36px rgba(124, 58, 237, 0.45), 0 2px 12px rgba(236, 72, 153, 0.35)',
  amber: '0 10px 36px rgba(245, 158, 11, 0.45), 0 2px 12px rgba(236, 72, 153, 0.3)',
  teal: '0 10px 36px rgba(6, 182, 212, 0.45), 0 2px 12px rgba(56, 189, 248, 0.3)',
  emerald: '0 10px 36px rgba(34, 197, 94, 0.45), 0 2px 12px rgba(132, 204, 22, 0.3)',
  pink: '0 10px 36px rgba(236, 72, 153, 0.45), 0 2px 12px rgba(217, 70, 239, 0.3)',
  indigo: '0 10px 36px rgba(99, 102, 241, 0.45), 0 2px 12px rgba(124, 58, 237, 0.3)',
};

export function FloatingActionButton({ onClick, label, gradient = 'purple' }: FloatingActionButtonProps) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={`
        fixed bottom-8 right-8
        fab-pill
        bg-gradient-to-r ${gradientMap[gradient]}
        text-white
        z-50
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2
      `}
      style={{ boxShadow: shadowMap[gradient] }}
    >
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );
}
